import { DevicePricingItem, PricingItem, QuoteService } from 'quote-domain-core';
import { PricingItemQuantityService } from '../pricing-item/pricing-item-quantity-service';
import { singleton } from 'tsyringe';

@singleton()
export class QuoteServiceQuantityService {
    constructor(private readonly pricingItemQuantityService: PricingItemQuantityService) {
    }

    public fillTotalQuantityWithBaseQuantity(quoteService: QuoteService): void {
        this.forEachPricingItem(quoteService, pricingItem => {
            this.pricingItemQuantityService.fillTotalQuantityWithBaseQuantity(pricingItem);
        });
    }

    public mergeAndUpdateTotalQuantityByArticleCode(quoteService: QuoteService): void {
        const quantityMap = this.mergeQuantityByArticleCode(quoteService);
        this.forEachPricingItem(quoteService, pricingItem => {
            this.pricingItemQuantityService.updateTotalQuantityGroupedByArticleCode(pricingItem, quantityMap);
        });
    }

    public mergeAndUpdateTotalQuantityByGroupingCode(quoteService: QuoteService): void {
        const allPricingItems: PricingItem[] = [];
        (quoteService.customGroups ?? []).forEach(customGroup => {
            allPricingItems.push(...(customGroup.pricingItems ?? []));
        });

        const quantityMap = new Map<any, number>();
        const grouped = new Map<any, { quantities: number[] }>();

        allPricingItems.forEach(pricingItem => {
            const item = this.pricingItemQuantityService.buildGroupedQuantityKey(pricingItem);
            if (item.key === undefined) {
                return;
            }

            if (!grouped.has(item.key)) {
                grouped.set(item.key, { quantities: [item.hasGroupedOrCmc ? 0 : item.quantity] });
                return;
            }

            grouped.get(item.key)!.quantities.push(item.hasGroupedOrCmc ? 0 : item.quantity);
        });

        grouped.forEach((value, key) => {
            quantityMap.set(key, value.quantities.reduce((sum, quantity) => sum + quantity, 0));
        });

        let globalGroupedMax = 0;
        grouped.forEach((_, key) => {
            globalGroupedMax = Math.max(globalGroupedMax, quantityMap.get(key));
        });
        grouped.forEach((_, key) => {
            quantityMap.set(key, globalGroupedMax);
        });

        allPricingItems.forEach(pricingItem => {
            if (pricingItem instanceof DevicePricingItem) {
                this.pricingItemQuantityService.updateTotalQuantityGroupedByGroupingCode(pricingItem, quantityMap);
            }
        });
    }

    private mergeQuantityByArticleCode(quoteService: QuoteService): Map<any, number> {
        const quantityMap: Map<any, number> = new Map();
        this.forEachPricingItem(quoteService, pricingItem => {
            this.pricingItemQuantityService.mergeQuantityByArticleCode(pricingItem, quantityMap);
        });
        return quantityMap;
    }

    private forEachPricingItem(quoteService: QuoteService, callback: (pricingItem: PricingItem) => void): void {
        (quoteService.customGroups ?? []).forEach(customGroup => {
            (customGroup.pricingItems ?? []).forEach(callback);
        });
    }
}
